import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Box,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { MortgageTrack, MortgageType, MortgageTrackRepaymentType } from "../../interfaces/DTO/mortgage/mortgage";
import {
  formatMortgageTrackType,
  formatMortgageTrackRepaymentType,
  formatMortgageInterestBaseType,
  formatMortgageTrackIndexationType
} from "../../utils/mortgageUtil";
import { formatCurrency } from "../../utils/namesFormater";
import { calculateMortgagePayments, MortgagePayment } from "../../services/mortgageCalculator";
import PercentageTextField from "../../shared/UIParts/InputFields/PercentageTextField";
import { formatTimeUntilDate, formatTimeSinceDate } from "../../utils/dateCalculators";
import { buildAnnualSchedule, deriveMonthsRemaining } from "../../mortgage/mortgageCalculator";
import { formatSensitivityBucket } from "../../mortgage/formatSensitivityBuckets";
import { Chip } from "@mui/material";

interface TrackWithAmount extends MortgageTrack {
  calculatedAmount: number;
}

interface MortgageTracksTableProps {
  tracks: TrackWithAmount[];
  selectedBasketType: MortgageType;
  defaultPeriodYears: number;
  selectedRepaymentType: MortgageTrackRepaymentType;
  trackPayments: Map<number, MortgagePayment[]>;
  startDate?: string; // תאריך התחלת המשכנתא (ISO format: YYYY-MM-DD)
  reportDate?: string; // תאריך הדוח / מועד חישוב (ISO format: YYYY-MM-DD)
  onUpdatePercentage?: (trackIndex: number, newPercentage: number) => void;
  onUpdateInterestRate?: (trackIndex: number, newRate: number) => void;
  onUpdateInterestMargin?: (trackIndex: number, newMargin: number) => void;
  onDeleteTrack?: (trackIndex: number) => void;
  onUpdatePeriodYears?: (newPeriodYears: number) => void;
  onUpdateTrackMonths?: (trackIndex: number, newMonths: number) => void;
  isEditable?: boolean;
}

const MortgageTracksTable: React.FC<MortgageTracksTableProps> = ({
  tracks,
  selectedBasketType,
  defaultPeriodYears,
  selectedRepaymentType,
  trackPayments,
  startDate,
  reportDate,
  onUpdatePercentage,
  onUpdateInterestRate,
  onUpdateInterestMargin,
  onDeleteTrack,
  onUpdatePeriodYears,
  onUpdateTrackMonths,
  isEditable = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTrackIndex, setSelectedTrackIndex] = useState<number | null>(null);
  const [hoveredPeriodCell, setHoveredPeriodCell] = useState<number | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, trackIndex: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedTrackIndex(trackIndex);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTrackIndex(null);
  };

  const handleDeleteTrack = () => {
    if (selectedTrackIndex !== null && onDeleteTrack) {
      onDeleteTrack(selectedTrackIndex);
    }
    handleMenuClose();
  };
  // חישוב סכום כולל צפוי לכל מסלול
  const totalPaymentByTrack = useMemo(() => {
    return tracks.map((_, index) => {
      const payments = trackPayments.get(index);
      if (!payments || payments.length === 0) return 0;
      return payments.reduce((sum, payment) => sum + payment.totalPayment, 0);
    });
  }, [tracks, trackPayments]);

  // חישוב תשלום חודשי מקסימלי לכל מסלול
  const maxMonthlyPaymentByTrack = useMemo(() => {
    return tracks.map((_, index) => {
      const payments = trackPayments.get(index);
      if (!payments || payments.length === 0) return 0;
      return Math.max(...payments.map(p => p.totalPayment));
    });
  }, [tracks, trackPayments]);

  // חישוב סכומים כוללים לאחוזים
  const totalOriginalAmount = useMemo(() => {
    return tracks.reduce((sum, track) => sum + (track.original_amount || 0), 0);
  }, [tracks]);

  const totalCurrentBalance = useMemo(() => {
    return tracks.reduce((sum, track) => sum + (track.current_balance || 0), 0);
  }, [tracks]);

  // חישוב עלות 100 ש"ח קרן ודירוג סיכון לשנה הנוכחית לכל מסלול
  const costPer100ByTrack = useMemo(() => {
    return tracks.map((track) => {
      // בדיקה שיש את כל הנתונים הדרושים
      if (!track.current_balance || track.current_balance <= 0) return null;
      if (track.interest_rate == null) return null;
      if (track.repayment_type !== 'SPITZER') return null;

      // חישוב חודשים נותרים - משתמש ב-reportDate אם קיים, אחרת startDate
      const dateToUse = reportDate || startDate;
      const monthsRemaining = deriveMonthsRemaining(track, dateToUse);
      if (!monthsRemaining || monthsRemaining <= 0) return null;

      // בניית לוח פירעון שנתי
      const schedule = buildAnnualSchedule({
        currentBalance: track.current_balance,
        annualRatePct: track.interest_rate,
        monthsRemaining,
        monthlyPayment: track.monthly_payment,
      });

      // החזרת costPer100 ו-sensitivityPctOfPayment של השנה הראשונה (שנה נוכחית)
      if (schedule.length > 0) {
        return {
          costPer100: schedule[0].costPer100,
          sensitivityPct: schedule[0].sensitivityPctOfPayment,
        };
      }

      return null;
    });
  }, [tracks, startDate, reportDate]);

  const rowData = [
    {
      label: "מטרת ההלוואה",
      values: tracks.map((track) => track.loan_purpose || "-"),
      editable: false,
    },
    {
      label: "שם המסלול",
      values: tracks.map((track) => formatMortgageTrackType(track.track_type)),
      editable: false,
    },
    {
      label: "אחוז מהמשכנתא",
      values: tracks.map((track, index) => (
        isEditable && onUpdatePercentage ? (
          <PercentageTextField
            key={index}
            size="small"
            value={(track.amountPercentage || 0) * 100}
            onChange={(value) => onUpdatePercentage(index, value)}
            max={100}
            min={0}
            decimalPlaces={1}
          />
        ) : (
          <Typography key={index} variant="body2">
            {((track.amountPercentage || 0) * 100).toFixed(1)}%
          </Typography>
        )
      )),
      editable: true,
    },
    {
      label: "סכום במסלול",
      values: tracks.map((track) => (
        <Typography key={track.track_type} variant="body2" fontWeight="bold">
          {formatCurrency(track.calculatedAmount)}
        </Typography>
      )),
      editable: false,
    },
    {
      label: "סכום מקורי",
      values: tracks.map((track) => {
        if (!track.original_amount) {
          return <Typography key={track.track_type} variant="body2">-</Typography>;
        }
        const percentage = totalOriginalAmount > 0 ? (track.original_amount / totalOriginalAmount) * 100 : 0;
        return (
          <Typography key={track.track_type} variant="body2">
            {formatCurrency(track.original_amount)} <span style={{ color: '#666', fontSize: '0.85em' }}>({percentage.toFixed(1)}%)</span>
          </Typography>
        );
      }),
      editable: false,
    },
    {
      label: "יתרה נוכחית",
      values: tracks.map((track) => {
        if (!track.current_balance) {
          return <Typography key={track.track_type} variant="body2">-</Typography>;
        }
        const percentage = totalCurrentBalance > 0 ? (track.current_balance / totalCurrentBalance) * 100 : 0;
        return (
          <Typography key={track.track_type} variant="body2">
            {formatCurrency(track.current_balance)} <span style={{ color: '#666', fontSize: '0.85em' }}>({percentage.toFixed(1)}%)</span>
          </Typography>
        );
      }),
      editable: false,
    },
    {
      label: "תשלום חודשי (מהמסמך)",
      values: tracks.map((track) => (
        <Typography
          key={track.track_type}
          variant="h6"
          sx={{
            fontWeight: 'bold',
            fontSize: '1.1rem'
          }}
        >
          {track.monthly_payment ? formatCurrency(track.monthly_payment) : "-"}
        </Typography>
      )),
      editable: false,
    },
    {
      label: "עלות 100₪ קרן (שנה נוכחית)",
      values: tracks.map((track, index) => {
        const trackData = costPer100ByTrack[index];
        if (trackData === null || trackData === undefined) {
          return <Typography key={index} variant="body2">-</Typography>;
        }

        // Check if this is a FIXED track (no sensitivity)
        const isFixed = track.track_type === 'FIXED_LINKED' || track.track_type === 'FIXED_UNLINKED';

        // If FIXED, only show the cost per 100
        if (isFixed || trackData.sensitivityPct === 0) {
          return (
            <Typography
              key={index}
              variant="body2"
              sx={{
                fontWeight: 'bold',
                color: 'info.main'
              }}
            >
              {formatCurrency(trackData.costPer100 || 0)}
            </Typography>
          );
        }

        // For non-FIXED tracks, show cost + chip + sensitivity %
        const bucket = formatSensitivityBucket(trackData.sensitivityPct);

        return (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                color: 'info.main'
              }}
            >
              {formatCurrency(trackData.costPer100 || 0)}
            </Typography>
            <Chip
              label={bucket.labelHe}
              size="small"
              sx={{
                bgcolor: bucket.bgColor,
                color: bucket.textColor,
                fontWeight: 'bold',
                fontSize: '0.7rem',
                height: '20px',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.7rem',
              }}
            >
              {trackData.sensitivityPct.toFixed(1)}%
            </Typography>
          </Box>
        );
      }),
      editable: false,
    },
    {
      label: "מועד שינוי ריבית קרוב",
      values: tracks.map((track) => {
        if (!track.next_interest_change_date) return "-";
        const date = new Date(track.next_interest_change_date);
        const formattedDate = date.toLocaleDateString("he-IL");
        const timeUntil = formatTimeUntilDate(track.next_interest_change_date);

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2">{formattedDate}</Typography>
            {timeUntil && (
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'purple' }}>
                עוד {timeUntil}
              </Typography>
            )}
          </Box>
        );
      }),
      editable: false,
    },
    {
      label: "מועד תשלום ראשון",
      values: tracks.map((track) => {
        if (!track.first_payment_date) return "-";
        const date = new Date(track.first_payment_date);
        const formattedDate = date.toLocaleDateString("he-IL");
        const timeSince = formatTimeSinceDate(track.first_payment_date);

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2">{formattedDate}</Typography>
            {timeSince && (
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                לפני {timeSince}
              </Typography>
            )}
          </Box>
        );
      }),
      editable: false,
    },
    {
      label: "מועד סילוק צפוי",
      values: tracks.map((track) => {
        if (!track.expected_end_date) return "-";
        const date = new Date(track.expected_end_date);
        const formattedDate = date.toLocaleDateString("he-IL");
        const timeUntil = formatTimeUntilDate(track.expected_end_date);

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2">{formattedDate}</Typography>
            {timeUntil && (
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                עוד {timeUntil}
              </Typography>
            )}
          </Box>
        );
      }),
      editable: false,
    },
    {
      label: "תקופת ההלוואה בשנים",
      values: tracks.map((track, index) => {
        const trackYears = track.months ? Math.round(track.months / 12) : defaultPeriodYears;
        return (
          <Box
            key={index}
            onMouseEnter={() => setHoveredPeriodCell(index)}
            onMouseLeave={() => setHoveredPeriodCell(null)}
            sx={{
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {isEditable && onUpdateTrackMonths && hoveredPeriodCell === index ? (
              <>
                <IconButton
                  size="small"
                  onClick={() => {
                    const currentMonths = track.months || defaultPeriodYears * 12;
                    onUpdateTrackMonths(index, Math.max(12, currentMonths - 12));
                  }}
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: "action.hover",
                    "&:hover": {
                      bgcolor: "action.selected",
                    },
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2" sx={{ minWidth: 30, textAlign: "center" }}>
                  {trackYears}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    const currentMonths = track.months || defaultPeriodYears * 12;
                    onUpdateTrackMonths(index, currentMonths + 12);
                  }}
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: "action.hover",
                    "&:hover": {
                      bgcolor: "action.selected",
                    },
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              <Typography variant="body2">{trackYears}</Typography>
            )}
          </Box>
        );
      }),
      editable: true,
    },
    {
      label: "אופן התשלום לאורך התקופה",
      values: tracks.map(() => formatMortgageTrackRepaymentType(selectedRepaymentType)),
      editable: false,
    },
    {
      label: "שיעור הריבית השנתית",
      values: tracks.map((track, index) => (
        isEditable && onUpdateInterestRate ? (
          <PercentageTextField
            key={index}
            size="small"
            value={track.interest_rate || 0}
            onChange={(value) => onUpdateInterestRate(index, value)}
            max={20}
            min={0}
            decimalPlaces={2}
          />
        ) : (
          <Typography
            key={index}
            variant="h6"
            sx={{
              fontWeight: 'bold',
            }}
          >
            {track.interest_rate?.toFixed(2)}%
          </Typography>
        )
      )),
      editable: true,
      highlightRow: true, // סימון מיוחד לשורה זו
    },
    {
      label: "מנגנון קביעת הריבית",
      values: tracks.map((track, index) => {
        if (!track.interest_base_type) return "-";

        const baseTypeName = formatMortgageInterestBaseType(track.interest_base_type);

        // אם זה FIXED, רק נציג את השם
        if (track.interest_base_type === "FIXED") {
          return baseTypeName;
        }

        // אחרת, נציג את השם + שדה עריכה למרווח
        return (
          <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">{baseTypeName}</Typography>
            {isEditable && onUpdateInterestMargin ? (
              <PercentageTextField
                size="small"
                value={track.interest_margin || 0}
                onChange={(value) => onUpdateInterestMargin(index, value)}
                max={5}
                min={-5}
                decimalPlaces={2}
                sx={{ maxWidth: "90px" }}
              />
            ) : track.interest_margin !== undefined && track.interest_margin !== 0 ? (
              <Typography variant="caption" color="text.secondary">
                {track.interest_margin > 0 ? '+' : ''}{track.interest_margin.toFixed(2)}%
              </Typography>
            ) : null}
          </Box>
        );
      }),
      editable: true,
    },
    {
      label: "תדירות עדכון ריבית (שנים)",
      values: tracks.map((track) =>
        track.interest_update_interval_years && track.interest_update_interval_years > 0
          ? track.interest_update_interval_years.toString()
          : ""
      ),
      editable: false,
    },
    {
      label: selectedRepaymentType === MortgageTrackRepaymentType.SPITZER
        ? "סוג הצמדה (משפיעה על ההחזר החודשי)"
        : "סוג הצמדה",
      values: tracks.map((track) =>
        track.indexation_type ? formatMortgageTrackIndexationType(track.indexation_type) : "-"
      ),
      editable: false,
    },
    {
      label: "עדכון ריבית הבא",
      values: tracks.map((track) => {
        // אם אין תדירות עדכון או שהיא 0, הריבית קבועה
        if (!track.interest_update_interval_years || track.interest_update_interval_years === 0) {
          return "-";
        }

        if (!startDate) {
          return "-";
        }

        // חישוב תאריך העדכון הבא
        const start = new Date(startDate);
        const interestUpdateIntervalMonths = Math.round(track.interest_update_interval_years * 12);

        // תאריך העדכון הראשון
        const nextUpdateDate = new Date(start);
        nextUpdateDate.setMonth(nextUpdateDate.getMonth() + interestUpdateIntervalMonths);

        // חישוב ההפרש בין עכשיו לתאריך העדכון
        const now = new Date();
        const monthsDiff = Math.round((nextUpdateDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 30.44));

        // אם התאריך כבר עבר, נמצא את העדכון הבא
        let adjustedNextUpdate = new Date(nextUpdateDate);
        while (adjustedNextUpdate <= now) {
          adjustedNextUpdate.setMonth(adjustedNextUpdate.getMonth() + interestUpdateIntervalMonths);
        }

        const adjustedMonthsDiff = Math.round((adjustedNextUpdate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 30.44));

        // פורמט התאריך
        const year = adjustedNextUpdate.getFullYear();

        // אם העדכון הבא הוא תוך פחות משנה (12 חודשים)
        if (adjustedMonthsDiff < 12) {
          return `עוד ${adjustedMonthsDiff} חודשים (${year})`;
        }

        // אם העדכון הבא הוא שנה או יותר
        const years = Math.floor(adjustedMonthsDiff / 12);
        const remainingMonths = adjustedMonthsDiff % 12;

        if (remainingMonths === 0) {
          return `עוד ${years} ${years === 1 ? 'שנה' : 'שנים'} (${year})`;
        } else {
          return `עוד ${years} ${years === 1 ? 'שנה' : 'שנים'} ו-${remainingMonths} חודשים (${year})`;
        }
      }),
      editable: false,
    },
    {
      label: "החזר חודשי (חודש ראשון)",
      values: tracks.map((_, index) => {
        const payments = trackPayments.get(index);
        if (!payments || payments.length === 0) return "-";
        return formatCurrency(payments[0].totalPayment);
      }),
      editable: false,
    },
    {
      label: "החזר חודשי גבוה ביותר",
      values: tracks.map((_, index) => {
        const maxPayment = maxMonthlyPaymentByTrack[index];
        return maxPayment > 0 ? formatCurrency(maxPayment) : "-";
      }),
      editable: false,
    },
    {
      label: "סה״כ צפוי לתשלום",
      values: tracks.map((_, index) => {
        const total = totalPaymentByTrack[index];
        return total > 0 ? formatCurrency(total) : "-";
      }),
      editable: false,
    },
    {
      label: "עמלת פירעון מוקדם",
      values: tracks.map(() => "-"),
      editable: false,
    },
  ];

  return (
    <TableContainer component={Paper} elevation={2} sx={{ mt: 3 }}>
      <Table sx={{ minWidth: 650 }} size="small">
        <TableHead>
          <TableRow>
            <TableCell
              align="right"
              sx={{
                fontWeight: "bold",
                bgcolor: "primary.main",
                color: "white",
                minWidth: "200px",
              }}
            >
              <Box sx={{ textAlign: "right", direction: "rtl" }}>
                שדה
              </Box>
            </TableCell>
            {tracks.map((_, index) => (
              <TableCell
                key={index}
                align="right"
                sx={{
                  fontWeight: "bold",
                  bgcolor: "primary.main",
                  color: "white",
                }}
              >

                  {isEditable && tracks.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, index)}
                      sx={{ color: "white" }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  )}
                  <span>מסלול {index + 1}</span>

              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rowData.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              sx={{
                "&:nth-of-type(odd)": { bgcolor: (row as any).highlightRow ? "rgba(0, 188, 212, 0.15)" : "action.hover" },
                "&:nth-of-type(even)": { bgcolor: (row as any).highlightRow ? "rgba(0, 188, 212, 0.15)" : "inherit" },
                "&:hover": { bgcolor: (row as any).highlightRow ? "rgba(0, 188, 212, 0.25)" : "action.selected" },
                bgcolor: (row as any).highlightRow ? "rgba(0, 188, 212, 0.15)" : undefined,
              }}
            >
              <TableCell
                component="th"
                scope="row"
                align="right"
                sx={{ fontWeight: "bold", color: "text.primary" }}
              >
                {row.label}
              </TableCell>
              {row.values.map((value, colIndex) => (
                <TableCell key={colIndex} align="right">
                  {typeof value === "string" ? (
                    <Typography variant="body2">{value}</Typography>
                  ) : (
                    value
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* תפריט מחיקת מסלול */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={handleDeleteTrack}
          sx={{ color: "error.main" }}
        >
          מחק מסלול
        </MenuItem>
      </Menu>
    </TableContainer>
  );
};

export default MortgageTracksTable;
